import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import api from '~/services/api';
import { IActTeamMember } from '~/services/api/actTeamMember/types';
import { IDictionary } from '~/services/api/dictionaries/types';
import { ILocalAdminForm } from '~/services/api/localAdmin/types';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useRole from '~/store/user/hooks/useRole';
import { IOption, IUserRole } from '~/types';
import memberOptions, { MemberType } from '~/ui/constants/memberTypes';
import { extractErrorMessage } from '~/utils/error/error';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';

const TEAM_IDS_SET_OPTS = { shouldValidate: true, shouldDirty: true } as const;

interface UseGuTeamAssignmentParams {
  defaultValues?: ILocalAdminForm;
  primaryRole?: string | null;
  setValue: UseFormSetValue<ILocalAdminForm>;
}

const useGuTeamAssignment = ({
  defaultValues,
  primaryRole,
  setValue,
}: UseGuTeamAssignmentParams) => {
  const showError = useStoreActions(actions => actions.snackbar.showError);
  const onGetActTeamsAvailable = useStoreActions(actions => actions.actTeam.onGetActTeamsAvailable);
  const availableActTeams = useStoreState(state => state.actTeam.availableActTeams);

  const [clinicWideActTeams, setClinicWideActTeams] = useState<IDictionary[] | null>(null);
  const [teamsFetching, setTeamsFetching] = useState(false);

  const clinic = useStoreState(state => state.clinic.current);
  const teamMember = useStoreState(state => state.actTeamMember.current);

  const { isLocalAdmin, isProgramAssistant, role, team } = useRole();

  const paAccessibleTeamSet = useMemo(() => {
    const ids = role?.teams?.map(t => t.id) ?? [];
    if (ids.length) return new Set(ids);
    if (team?.id) return new Set([team.id]);
    return new Set<number>();
  }, [role?.teams, team?.id]);

  const teamsForGlobalUserSelect = clinicWideActTeams ?? availableActTeams;

  const teamOptions = useMemo(
    () => formatDictionaryOptions(teamsForGlobalUserSelect),
    [teamsForGlobalUserSelect],
  );

  const currentClinicRoles = (teamMember as IActTeamMember)?.roles?.filter(
    memberRole => memberRole.clinic.id === Number(clinic?.id),
  );

  const isGlobalUserFromRoles = !!currentClinicRoles?.find(
    memberRole => memberRole.name === IUserRole.GlobalUser,
  );
  const isGlobalUserFromDefaults = defaultValues?.primaryRole === MemberType.GLOBAL_USER;
  const isGlobalUser = isGlobalUserFromRoles || Boolean(isGlobalUserFromDefaults);

  const currentTeamMember = defaultValues?.primaryRole === MemberType.TEAM_MEMBER;
  const currentTeamLeader = defaultValues?.primaryRole === MemberType.TEAM_LEADER;

  const filteredMemberOptions = useMemo(() => {
    if (currentTeamMember) {
      return memberOptions.filter(o => o.value !== MemberType.TEAM_LEADER);
    }
    if (currentTeamLeader) {
      return memberOptions.filter(o => o.value !== MemberType.TEAM_MEMBER);
    }
    return memberOptions;
  }, [currentTeamLeader, currentTeamMember]);

  const isGlobalUserSelected = primaryRole === MemberType.GLOBAL_USER;

  const restrictGuTeamsForPa = Boolean(isProgramAssistant && !isLocalAdmin && isGlobalUserSelected);

  /** Existing GU assignments outside PA scope — PA cannot drop these (persist after clear / chips). */
  const lockedTeamIdsForPa = useMemo(() => {
    if (!isProgramAssistant || isLocalAdmin) return [] as number[];
    const ids = defaultValues?.teamIds ?? [];
    return ids.filter(id => !paAccessibleTeamSet.has(id));
  }, [defaultValues?.teamIds, isLocalAdmin, isProgramAssistant, paAccessibleTeamSet]);

  const fetchAvailableTeams = useCallback(async () => {
    if (!(isGlobalUser || isGlobalUserSelected) || !clinic?.id) {
      setClinicWideActTeams(null);
      return;
    }
    try {
      setTeamsFetching(true);
      if (restrictGuTeamsForPa) {
        const { data } = await api.actTeam.getClinicActTeams(String(clinic.id), {
          pageNumber: 1,
          pageSize: 5000,
          showAll: true,
        });
        setClinicWideActTeams(data.items.map(({ id, name }) => ({ id, name })));
      } else {
        setClinicWideActTeams(null);
        await onGetActTeamsAvailable({ clinicId: String(clinic.id) });
      }
    } catch (e) {
      showError(extractErrorMessage(e));
      setClinicWideActTeams(null);
    } finally {
      setTeamsFetching(false);
    }
  }, [
    clinic?.id,
    isGlobalUser,
    isGlobalUserSelected,
    onGetActTeamsAvailable,
    restrictGuTeamsForPa,
    showError,
  ]);

  useEffect(() => {
    fetchAvailableTeams();
  }, [fetchAvailableTeams]);

  const setAll = useCallback(
    (all?: boolean) => {
      if (restrictGuTeamsForPa) {
        const list = teamsForGlobalUserSelect;
        if (all && !list?.length && lockedTeamIdsForPa.length === 0) return;
        if (all) {
          const inList = new Set(list.map(t => t.id));
          const accessibleInList = Array.from(paAccessibleTeamSet).filter(id => inList.has(id));
          setValue(
            'teamIds',
            Array.from(new Set([...lockedTeamIdsForPa, ...accessibleInList])),
            TEAM_IDS_SET_OPTS,
          );
        } else {
          setValue('teamIds', [...lockedTeamIdsForPa], TEAM_IDS_SET_OPTS);
        }
      } else if (all) {
        if (!teamsForGlobalUserSelect?.length) return;
        setValue(
          'teamIds',
          teamsForGlobalUserSelect.map(m => m.id),
          TEAM_IDS_SET_OPTS,
        );
      } else {
        setValue('teamIds', [], TEAM_IDS_SET_OPTS);
      }
    },
    [
      lockedTeamIdsForPa,
      paAccessibleTeamSet,
      restrictGuTeamsForPa,
      setValue,
      teamsForGlobalUserSelect,
    ],
  );

  const normalizeTeamIdsMultiValue = useCallback(
    (next: Array<string | number>): Array<string | number> => {
      if (!restrictGuTeamsForPa || lockedTeamIdsForPa.length === 0) return next;
      const ids = new Set(next.map(Number));
      lockedTeamIdsForPa.forEach(id => ids.add(id));
      return Array.from(ids);
    },
    [lockedTeamIdsForPa, restrictGuTeamsForPa],
  );

  const isTeamOptionDisabledForPa = useCallback(
    (option: IOption) => {
      if (!restrictGuTeamsForPa) return false;
      const id = Number(option.value);
      if (lockedTeamIdsForPa.includes(id)) return true;
      return !paAccessibleTeamSet.has(id);
    },
    [lockedTeamIdsForPa, paAccessibleTeamSet, restrictGuTeamsForPa],
  );

  const showPrimaryRoleTeams =
    isLocalAdmin || (Boolean(isProgramAssistant) && Boolean(isGlobalUser));

  return {
    isGlobalUserSelected,
    filteredMemberOptions,
    teamOptions,
    restrictGuTeamsForPa,
    setAll,
    isTeamOptionDisabledForPa,
    normalizeTeamIdsMultiValue,
    teamsFetching,
    showPrimaryRoleTeams,
  };
};

export default useGuTeamAssignment;
